import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import PageLoading from '../components/PageLoading';
import { useTeamPhotos } from '../context/TeamPhotosContext';
import { CONFIG } from '../config';
import { usePaLabels } from '../context/PaLabelsContext';
import { localizeArticleRow } from '../lib/localizedContent';
import { pathForGoTo } from '../lib/siteRoutes';
import { formatMonthYear } from '../utils/format';

export default function HomePage() {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const formRef = useRef(null);
  const { photoFor } = useTeamPhotos();
  const { labelFor, navPracticeLinks } = usePaLabels();
  const [recent, setRecent] = useState([]);
  const [homeUpdatesStatus, setHomeUpdatesStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setHomeUpdatesStatus('loading');
      const { fetchRecentPublishedArticles } = await import('../lib/articlesApi');
      const { articles, listError } = await fetchRecentPublishedArticles(3);
      if (cancelled) return;
      if (listError) {
        console.warn('[home updates]', listError);
      }
      setRecent(articles ?? []);
      setHomeUpdatesStatus('ready');
    };
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(() => load(), { timeout: 3000 });
      return () => {
        cancelled = true;
        cancelIdleCallback(id);
      };
    }
    const t = setTimeout(load, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, []);

  // Mirrors handleFormSubmit() in index-fixed.html exactly.
  const handleFormSubmit = async (btn) => {
    const FORMSPREE_URL = CONFIG.FORMSPREE_URL;

    const form = formRef.current;
    if (!form) { console.error('Form container not found — check wrapper class'); return; }

    const data = {};
    form.querySelectorAll('input, select, textarea').forEach(el => {
      const label = el.closest('.form-group')?.querySelector('label')?.textContent?.trim()
                 || el.placeholder || el.name || 'Field';
      if (el.value.trim()) data[label] = el.value.trim();
    });

    if (Object.keys(data).length === 0) {
      alert(t('ui.fillOneField'));
      return;
    }

    const orig = btn.textContent;
    btn.textContent = t('ui.sending');
    btn.disabled = true;

    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        btn.textContent = t('ui.messageSent');
        btn.style.background = 'var(--gold)';
        btn.style.color = 'var(--navy)';
        form.querySelectorAll('input, select, textarea').forEach(el => { el.value = ''; });
        setTimeout(() => {
          btn.textContent = orig;
          btn.style.background = '';
          btn.style.color = '';
          btn.disabled = false;
        }, 4000);
      } else {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Server error ' + res.status);
      }
    } catch (e) {
      console.error('Formspree error:', e);
      btn.textContent = t('ui.formError');
      btn.style.background = '#c0392b';
      btn.style.color = '#fff';
      setTimeout(() => {
        btn.textContent = orig;
        btn.style.background = '';
        btn.style.color = '';
        btn.disabled = false;
      }, 4000);
    }
  };

  // Open article preview from home — navigate to the article route.
  const openHomeArticle = (id) => {
    navigate('/updates/' + id);
  };

  const goTo = (page) => navigate(pathForGoTo(page));

  const follow = (page) => (e) => {
    e.preventDefault();
    goTo(page);
  };

  const PRACTICE_AREAS = useMemo(
    () => [
      { page: 'pa-corporate', number: '01', key: 'corporate', delay: undefined },
      { page: 'pa-licensing', number: '02', key: 'licensing', delay: '0.08s' },
      { page: 'pa-labour', number: '03', key: 'labour', delay: '0.16s' },
      { page: 'pa-privacy', number: '04', key: 'privacy', delay: '0.24s' },
      { page: 'pa-realestate', number: '05', key: 'realestate', delay: '0.32s' },
      { page: 'pa-disputes', number: '06', key: 'disputes', delay: '0.40s' },
    ].map(({ page, number, key, delay }) => ({
      page,
      number,
      delay,
      name: t(`home.practiceCards.${key}.name`),
      desc: t(`home.practiceCards.${key}.desc`),
    })),
    [t, i18n.language],
  );

  const industryKeys = [
    'garments', 'pharma', 'automotive', 'furniture', 'fmcg', 'insurance',
    'software', 'logistics', 'realestate', 'industrial', 'fintech', 'healthcare',
  ];

  return (
    <div id="page-home" className="page active">

      {/* Hero */}
      <section className="hero">
        <div className="hero-bg-pattern"></div>
        <div className="hero-accent"></div>
        <div className="hero-vertical-text">{t('home.heroLocation')}</div>
        <div className="hero-content">
          <div className="hero-eyebrow">
            <div className="hero-eyebrow-line"></div>
            <span className="hero-eyebrow-text">{t('home.heroEyebrow')}</span>
          </div>
          <h1 className="hero-title">
            <span className="hero-title-line hero-title-line--top">
              {t('home.heroTitle')}
            </span>
            <em className="hero-title-line hero-title-line--em">
              {t('home.heroTitleEm')}
            </em>
          </h1>
          <p className="hero-subtitle">{t('home.heroSubtitle')}</p>
          <div className="hero-badges">
            <div className="hero-badge">{t('home.badgeChambersCorp')}</div>
            <div className="hero-badge">{t('home.badgeChambersLabour')}</div>
            <div className="hero-badge">{t('home.badgeLegal500')}</div>
          </div>
          <div className="hero-actions">
            <button type="button" className="btn-primary" onClick={() => goTo('contact')}>{t('buttons.speak')}</button>
            <button type="button" className="btn-outline" onClick={() => goTo('practices')}>{t('buttons.explore')}</button>
          </div>
        </div>
        <div className="hero-scroll"><div className="hero-scroll-line"></div>{t('home.scrollHint')}</div>
      </section>

      {/* Rankings Strip */}
      <div className="rankings">
        <div className="rankings-inner reveal">
          <div className="ranking-item">
            <div className="ranking-label">{t('home.rankingsChambers')}</div>
            <div className="ranking-title"><span className="ranking-gold">{t('home.rankedFirm')}</span></div>
            <div className="ranking-desc">{t('home.rankingCorp')}<br />{t('home.rankingCorpYears')}</div>
          </div>
          <div className="ranking-item">
            <div className="ranking-label">{t('home.rankingsChambers')}</div>
            <div className="ranking-title"><span className="ranking-gold">{t('home.rankedFirm')}</span></div>
            <div className="ranking-desc">{t('home.rankingLabour')}<br />{t('home.rankingLabourYears')}</div>
          </div>
          <div className="ranking-item">
            <div className="ranking-label">{t('home.rankingsLegal500')}</div>
            <div className="ranking-title"><span className="ranking-gold">{t('home.leadingFirm')}</span></div>
            <div className="ranking-desc">{t('home.rankingCorp')}<br />2020 – 2026</div>
          </div>
          <div className="ranking-item">
            <div className="ranking-label">{t('home.founded')}</div>
            <div className="ranking-title"><span className="ranking-gold">{t('home.foundedYear')}</span></div>
            <div className="ranking-desc">{t('home.foundedDesc')}<br />{t('home.foundedTagline')}</div>
          </div>
        </div>
      </div>

      {/* Practice Areas */}
      <section className="section" id="practices">
        <div className="section-header reveal">
          <div className="section-eyebrow"><div className="section-eyebrow-line"></div><span className="section-eyebrow-text">{t('home.whatWeDo')}</span></div>
          <h2 className="section-title">{t('home.ourPracticeAreas')} <em>{t('home.ourPracticeAreasEm')}</em></h2>
          <p className="section-sub">{t('home.practiceAreasSub')}</p>
        </div>
        <div className="practice-grid">
          {PRACTICE_AREAS.map(({ page, number, name, desc, delay }) => (
            <div
              key={page}
              className="practice-card reveal"
              style={delay ? { transitionDelay: delay } : undefined}
            >
              <div className="practice-number">{number}</div>
              <div className="practice-name">{name}</div>
              <p className="practice-desc">{desc}</p>
              <a
                href={pathForGoTo(page)}
                className="practice-link"
                onClick={follow(page)}
                aria-label={t('home.learnMoreAbout', { name })}
              >
                {t('home.learnMoreAbout', { name })}
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Our Team Preview */}
      <section className="section section-alt" id="team">
        <div className="section-header reveal">
          <div className="section-eyebrow"><div className="section-eyebrow-line"></div><span className="section-eyebrow-text">{t('home.ourPartners')}</span></div>
          <h2 className="section-title">{t('home.rankedLawyers')} <em>{t('home.rankedLawyersEm')}</em></h2>
          <p className="section-sub">{t('home.partnersSub')}</p>
        </div>
        <div className="team-grid team-grid--partners-home">
          <div className="team-card reveal">
            <div className="team-avatar" style={{ overflow: 'hidden', padding: 0 }}>
              {photoFor('diep') && <img src={photoFor('diep')} alt="Dee (Diep) Hoang" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />}
            </div>
            <div className="team-info">
              <div className="team-name team-name-link" onClick={() => goTo('profile-diep')}>Dee (Diep) Hoang</div>
              <div className="team-title">{t('home.founderPartner')}</div>

              <p className="team-bio">{t('home.diepBio')}</p>

            </div>
          </div>
          <div className="team-card reveal" style={{ transitionDelay: '0.1s' }}>
            <div className="team-avatar" style={{ overflow: 'hidden', padding: 0 }}>
              {photoFor('michael') && <img src={photoFor('michael')} alt="Michael Lee" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />}
            </div>
            <div className="team-info">
              <div className="team-name team-name-link" onClick={() => goTo('profile-michael')}>Michael Lee</div>
              <div className="team-title">{t('home.partner')}</div>

              <p className="team-bio">{t('home.michaelBio')}</p>

            </div>
          </div>
          <div className="team-card reveal" style={{ transitionDelay: '0.2s' }}>
            <div className="team-avatar" style={{ overflow: 'hidden', padding: 0 }}>
              {photoFor('helen') && <img src={photoFor('helen')} alt="Helen (Trang) Nguyen" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }} />}
            </div>
            <div className="team-info">
              <div className="team-name team-name-link" onClick={() => goTo('profile-helen')}>Helen (Trang) Nguyen</div>
              <div className="team-title">{t('home.partner')}</div>

              <p className="team-bio">{t('home.helenBio')}</p>

            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="section" style={{ paddingBottom: '60px' }}>
        <div className="section-header reveal">
          <div className="section-eyebrow"><div className="section-eyebrow-line"></div><span className="section-eyebrow-text">{t('home.sectors')}</span></div>
          <h2 className="section-title">{t('home.industriesWeServe')} <em>{t('home.industriesWeServeEm')}</em></h2>
        </div>
        <div className="industries-wrap reveal">
          {industryKeys.map((key) => (
            <div key={key} className="industry-tag">{t(`home.industries.${key}`)}</div>
          ))}
        </div>
      </section>

      {/* Legal Updates Preview */}
      <section className="section section-alt" id="home-updates">
        <div className="section-header reveal">
          <div className="section-eyebrow"><div className="section-eyebrow-line"></div><span className="section-eyebrow-text">{t('home.insights')}</span></div>
          <h2 className="section-title">{t('home.latestUpdates')} <em>{t('home.latestUpdatesEm')}</em></h2>
          <p className="section-sub">{t('home.updatesSub')}</p>
        </div>
        <div className="updates-grid" id="home-updates-grid">
          {homeUpdatesStatus === 'loading' ? (
            <PageLoading variant="inline" message={t('updates.loading')} />
          ) : recent.length === 0 ? (
            <div
              className="reveal visible"
              style={{
                gridColumn: '1 / -1',
                textAlign: 'center',
                color: 'var(--gray-mid)',
                fontSize: '15px',
                padding: '48px 16px',
              }}
            >
              {t('updates.noPublished')}
            </div>
          ) : (
            recent.map((a, i) => {
              const article = localizeArticleRow(a, i18n.language);
              return (
              <div
                key={a.id}
                className="update-card reveal visible"
                style={i ? { transitionDelay: (i * 0.08).toFixed(2) + 's' } : undefined}
              >
                <div className="update-tag">{labelFor(a.practiceArea)}</div>
                <div className="update-date">{formatMonthYear(a.date, { empty: '', locale: i18n.language })}</div>
                <div className="update-title update-title-link" onClick={() => openHomeArticle(a.id)}>
                  {article.title}
                </div>
                <p className="update-excerpt">{article.excerpt}</p>
                <button type="button" className="update-link" onClick={() => openHomeArticle(a.id)}>
                  {t('buttons.readMore')}
                </button>
              </div>
            );
            })
          )}
        </div>
        <div style={{ textAlign: 'center', marginTop: '48px' }}>
          <button type="button" className="btn-dark" onClick={() => goTo('updates')}>{t('buttons.allLegalUpdates')}</button>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="reveal">
          <h2 className="cta-title">{t('home.ctaTitle')}<br /><em>{t('home.ctaTitleEm')}</em></h2>
          <p className="cta-sub">{t('home.ctaSub')}</p>
          <div className="cta-actions">
            <button type="button" className="btn-dark" onClick={() => goTo('contact')}>{t('buttons.scheduleConsultation')}</button>
            <button type="button" className="btn-gold-outline" onClick={() => goTo('toolkits')}>{t('buttons.exploreToolkits')}</button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section" id="contact">
        <div className="contact-inner">
          <div className="contact-info reveal">
            <div className="section-eyebrow"><div className="section-eyebrow-line"></div><span className="section-eyebrow-text">{t('home.contactEyebrow')}</span></div>
            <h2 className="section-title">{t('home.contactTitle')} <em>{t('home.contactTitleEm')}</em></h2>
            <p className="section-sub">{t('home.contactSub')}</p>
            <div className="contact-details">
              <div className="contact-detail">
                <div className="contact-detail-icon">📍</div>
                <div className="contact-detail-text">
                  <div className="contact-detail-label">{t('contact.address')}</div>
                  <div className="contact-detail-value">{t('contact.addressFull')}</div>
                </div>
              </div>
              <div className="contact-detail">
                <div className="contact-detail-icon">📞</div>
                <div className="contact-detail-text">
                  <div className="contact-detail-label">{t('contact.phoneLabel')}</div>
                  <div className="contact-detail-value">+84 28 7300 0864</div>
                </div>
              </div>
              <div className="contact-detail">
                <div className="contact-detail-icon">✉</div>
                <div className="contact-detail-text">
                  <div className="contact-detail-label">{t('contact.emailLabel')}</div>
                  <div className="contact-detail-value">contact@dilinh.com</div>
                </div>
              </div>
              <div className="contact-detail">
                <div className="contact-detail-icon">🌐</div>
                <div className="contact-detail-text">
                  <div className="contact-detail-label">{t('contact.websiteLabel')}</div>
                  <div className="contact-detail-value">dilinh.com</div>
                </div>
              </div>
            </div>
          </div>
          <div className="contact-form reveal" style={{ transitionDelay: '0.1s' }} ref={formRef}>
            <div className="form-title">{t('contact.formTitle')}</div>
            <div className="form-row">
              <div className="form-group"><label>{t('contact.firstName')}</label><input type="text" placeholder={t('contact.firstNamePlaceholder')} autoComplete="given-name" /></div>
              <div className="form-group"><label>{t('contact.lastName')}</label><input type="text" placeholder={t('contact.lastNamePlaceholder')} autoComplete="family-name" /></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label>{t('contact.email')}</label><input type="email" placeholder={t('contact.emailPlaceholder')} autoComplete="email" /></div>
              <div className="form-group"><label>{t('contact.phone')}</label><input type="tel" placeholder={t('contact.phonePlaceholder')} autoComplete="tel" /></div>
            </div>
            <div className="form-group">
              <label htmlFor="home-practice-area">{t('contact.practiceArea')}</label>
              <select id="home-practice-area" name="practice_area" defaultValue="">
                <option value="">{t('contact.selectPractice')}</option>
                {navPracticeLinks.map(({ slug, label }) => (
                  <option key={slug} value={slug}>
                    {label}
                  </option>
                ))}
                <option value="other">{t('contact.other')}</option>
              </select>
            </div>
            <div className="form-group">
              <label>{t('contact.message')}</label>
              <textarea placeholder={t('contact.messagePlaceholder')}></textarea>
            </div>
            <button type="button" className="form-submit" onClick={(e) => handleFormSubmit(e.currentTarget)}>{t('buttons.send')}</button>
          </div>
        </div>
      </section>

      <Footer variant="full" firmColumn="partners" />
    </div>
  );
}
